import { Link } from "react-router-dom";
import type { Athlete } from "@/data/athletes";
import { toExcerpt, toPlainText } from "@/lib/text";

/**
 * Featured athlete story. Uses only existing published athlete content.
 * Renders nothing unless an athlete has a real bio and a live profile.
 * Bio/tagline are rendered as plain text only — stored HTML is never injected here.
 */
const FeaturedStory = ({
  athletes,
  hasProfile,
}: {
  athletes: Athlete[];
  hasProfile: (slug: string) => boolean;
}) => {
  const featured = athletes.find(
    (a) => hasProfile(a.id) && toPlainText(a.bio).length > 80
  );
  if (!featured) return null;

  const bioExcerpt = toExcerpt(featured.bio, 320);
  const tagline = toPlainText(featured.tagline);


  return (
    <section className="py-14 md:py-20">
      <div className="container">
        <div className="grid lg:grid-cols-12 gap-8 items-center bg-card rounded-[1.75rem] p-6 md:p-10 shadow-card">
          {featured.image && (
            <div className="lg:col-span-4">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-muted">
                <img
                  src={featured.image}
                  alt={featured.name}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}
          <div className={featured.image ? "lg:col-span-8" : "lg:col-span-12"}>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              Athlete spotlight
            </span>
            <h2 className="text-4xl md:text-5xl mt-2 text-balance">{featured.name}</h2>
            <p className="text-sm uppercase tracking-wider text-muted-foreground mt-2">
              {[featured.sport, featured.country].filter(Boolean).join(" · ")}
            </p>
            {tagline && (
              <p className="serif text-xl mt-4">{tagline}</p>
            )}
            <p className="text-muted-foreground mt-4 max-w-2xl">{bioExcerpt}</p>

            <Link
              to={`/athlete/${featured.id}`}
              className="mt-6 inline-flex items-center min-h-[44px] px-6 py-3 rounded-full bg-secondary text-secondary-foreground text-sm font-semibold hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              Read her story →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedStory;
